import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type UploadPhase =
  | "idle"
  | "uploading"
  | "processing"
  | "complete"
  | "failed"
  | "error";

export interface UploadSummary {
  added: number;
  total: number;
  errors: number;
  message: string;
  hasValidationErrors: boolean;
}

export interface UploadProgressState {
  phase: UploadPhase;
  fileName: string;
  progress: number;
  summary: UploadSummary | null;
  jobId: string;
  shouldOpenModal: boolean;
  errorModalOpen: boolean;
  fileUploadResponse: any[];
  editableRows: any[];
  infoRows: any[];
  modalNumberOfRecords: number;
  uploadFile: File | null;
  cancelled: boolean;
  aborted: boolean;
  toBeDeletedCount: number;
  uploadMode: "complete" | "incremental";
  insertedAssetCodes: string[];
  deletedAssetCodes: string[];
  detailsModalOpen: boolean;
}

const initialState: UploadProgressState = {
  phase: "idle",
  fileName: "",
  progress: 0,
  summary: null,
  jobId: "",
  shouldOpenModal: false,
  errorModalOpen: false,
  fileUploadResponse: [],
  editableRows: [],
  infoRows: [],
  modalNumberOfRecords: 0,
  uploadFile: null,
  cancelled: false,
  aborted: false,
  toBeDeletedCount: 0,
  uploadMode: "incremental",
  insertedAssetCodes: [],
  deletedAssetCodes: [],
  detailsModalOpen: false,
};

const uploadProgressSlice = createSlice({
  name: "uploadProgress",
  initialState,
  reducers: {
    startUpload: (
      state,
      action: PayloadAction<{ fileName: string; file: File }>,
    ) => {
      state.phase = "uploading";
      state.fileName = action.payload.fileName;
      state.progress = 0;
      state.summary = null;
      state.jobId = "";
      state.shouldOpenModal = false;
      state.uploadFile = action.payload.file;
      state.errorModalOpen = false;
      state.fileUploadResponse = [];
      state.editableRows = [];
      state.infoRows = [];
      state.modalNumberOfRecords = 0;
      state.cancelled = false;
      state.aborted = false;
      state.insertedAssetCodes = [];
      state.deletedAssetCodes = [];
      state.detailsModalOpen = false;
    },

    updateProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },

    setUploadMode: (
      state,
      action: PayloadAction<"complete" | "incremental">,
    ) => {
      state.uploadMode = action.payload;
    },

    setProcessing: (state, action: PayloadAction<{ jobId: string }>) => {
      state.phase = "processing";
      state.progress = 0;
      state.jobId = action.payload.jobId;
    },

    setComplete: (
      state,
      action: PayloadAction<{
        summary: UploadSummary;
        insertedAssetCodes?: string[];
        deletedAssetCodes?: string[];
      }>,
    ) => {
      state.phase = "complete";
      state.progress = 100;
      state.summary = action.payload.summary;
      state.insertedAssetCodes = action.payload.insertedAssetCodes ?? [];
      state.deletedAssetCodes = action.payload.deletedAssetCodes ?? [];
    },

    openDetailsModal: (state) => {
      state.detailsModalOpen = true;
    },

    closeDetailsModal: (state) => {
      state.detailsModalOpen = false;
    },

    setFailed: (state, action: PayloadAction<{ summary: UploadSummary }>) => {
      state.phase = "failed";
      state.progress = 100;
      state.summary = action.payload.summary;
    },

    setError: (state) => {
      state.phase = "error";
      state.progress = 0;
    },

    // Normal close — used when upload is done/errored and user dismisses the toaster
    closeToaster: (state) => {
      state.phase = "idle";
      state.fileName = "";
      state.progress = 0;
      state.summary = null;
      state.jobId = "";
      state.shouldOpenModal = false;
      state.errorModalOpen = false;
      state.fileUploadResponse = [];
      state.editableRows = [];
      state.infoRows = [];
      state.modalNumberOfRecords = 0;
      state.uploadFile = null;
      state.cancelled = true;
      state.insertedAssetCodes = [];
      state.deletedAssetCodes = [];
      state.detailsModalOpen = false;
      // aborted intentionally NOT set here
    },

    // 👇 New: user confirmed abort while upload/processing was in progress
    abortUpload: (state) => {
      state.phase = "idle";
      state.fileName = "";
      state.progress = 0;
      state.summary = null;
      state.jobId = "";
      state.shouldOpenModal = false;
      state.errorModalOpen = false;
      state.fileUploadResponse = [];
      state.editableRows = [];
      state.infoRows = [];
      state.modalNumberOfRecords = 0;
      state.uploadFile = null;
      state.cancelled = true;
      state.insertedAssetCodes = [];
      state.deletedAssetCodes = [];
      state.detailsModalOpen = false;
      state.aborted = true; // 👈 lets consumers (sagas/thunks) react to an intentional abort
    },

    triggerViewSummary: (state) => {
      state.shouldOpenModal = true;
      state.errorModalOpen = true;
    },

    clearViewSummaryFlag: (state) => {
      state.shouldOpenModal = false;
    },

    openErrorModal: (
      state,
      action: PayloadAction<{
        fileUploadResponse: any[];
        editableRows: any[];
        infoRows?: any[];
        modalNumberOfRecords: number;
        toBeDeletedCount: number;
      }>,
    ) => {
      state.fileUploadResponse = action.payload.fileUploadResponse;
      state.editableRows = action.payload.editableRows;
      state.infoRows = action.payload.infoRows ?? [];
      state.modalNumberOfRecords = action.payload.modalNumberOfRecords;
      state.toBeDeletedCount = action.payload.toBeDeletedCount;
    },

    closeErrorModal: (state) => {
      state.errorModalOpen = false;
      state.fileUploadResponse = [];
      state.editableRows = [];
      state.infoRows = [];
      state.modalNumberOfRecords = 0;
    },

    resetUploadProgress: () => initialState,
  },
});

export const {
  startUpload,
  updateProgress,
  setProcessing,
  setComplete,
  setFailed,
  setError,
  closeToaster,
  abortUpload, // 👈 export
  triggerViewSummary,
  clearViewSummaryFlag,
  openErrorModal,
  closeErrorModal,
  resetUploadProgress,
  setUploadMode,
  openDetailsModal,
  closeDetailsModal,
} = uploadProgressSlice.actions;

export default uploadProgressSlice.reducer;
